/**
 * Hooks React-Query du module Revision Cards.
 *
 * QueryKeys (single source of truth — synchronisé avec `MissionControlPage`
 * qui lit la queue depuis `['training','today']`) :
 *  - `['training', 'cards', 'due', module?]` → liste due
 *  - `['training', 'cards', 'stats']` → KPI strip
 *
 * Sur review, on invalide ces 3 keys + `['training', 'today']` pour que le
 * compteur Mission Control reflète immédiatement la nouvelle valeur due.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Outcome } from '@/api/outcome';
import { revisionCardsApi } from './api';
import type { RevisionModule, ReviewResponse } from './types';

export function useDueCards(module?: RevisionModule) {
  return useQuery({
    queryKey: ['training', 'cards', 'due', module ?? null] as const,
    queryFn: () => revisionCardsApi.fetchDueCards(module),
  });
}

export function useCardStats() {
  return useQuery({
    queryKey: ['training', 'cards', 'stats'] as const,
    queryFn: () => revisionCardsApi.fetchCardStats(),
  });
}

interface ReviewVariables {
  cardId: number;
  outcome: Outcome;
  timeSpentMs: number;
}

export function useReviewMutation() {
  const qc = useQueryClient();
  return useMutation<ReviewResponse, unknown, ReviewVariables>({
    mutationFn: ({ cardId, outcome, timeSpentMs }) =>
      revisionCardsApi.postReview(cardId, outcome, timeSpentMs),
    onSuccess: () => {
      // 4 query keys touchées : la liste due (toutes variantes module),
      // les stats, et le compteur Mission Control. On invalide via prefix
      // pour ne pas avoir à énumérer les 5 valeurs possibles de `module`.
      void qc.invalidateQueries({ queryKey: ['training', 'today'] });
      void qc.invalidateQueries({ queryKey: ['training', 'cards', 'due'] });
      void qc.invalidateQueries({ queryKey: ['training', 'cards', 'stats'] });
    },
  });
}
